import { useCallback, useRef, useState } from 'react'

/**
 * Tracks whether an element has scrolled into view, for one-time scroll-triggered
 * reveals (not a live visibility toggle) — once true, stays true. Degrades to `true`
 * immediately when IntersectionObserver isn't available, so content is never stuck
 * permanently hidden because of a missing browser API.
 *
 * Usage: const [ref, inView] = useInView(); <div ref={ref} className={inView ? '...' : '...'} />
 */
export function useInView(
  options?: IntersectionObserverInit
): [(node: Element | null) => void, boolean] {
  const [inView, setInView] = useState(() => typeof IntersectionObserver === 'undefined')
  const observerRef = useRef<IntersectionObserver | null>(null)

  const ref = useCallback(
    (node: Element | null) => {
      observerRef.current?.disconnect()
      if (!node || typeof IntersectionObserver === 'undefined') return

      observerRef.current = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          observerRef.current?.unobserve(node)
        }
      }, options)
      observerRef.current.observe(node)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return [ref, inView]
}
